import { useState } from "react";
import { api, imgUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, ExternalLink, MousePointerClick, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

export function AffiliateCard({ card, isAdmin, onEdit, onDelete, index }) {
  const media = card.media && card.media.length ? card.media : [];
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const current = media[active];

  const go = (dir, e) => {
    e?.stopPropagation();
    setActive((a) => (a + dir + media.length) % media.length);
  };

  const trackClick = () => {
    api.post(`/cards/${card.id}/click`).catch(() => {});
  };

  return (
    <div data-testid={`affiliate-card-${card.id}`}
      className="flex flex-col bg-[#121212] border border-white/10 hover:border-white/30 transition-all duration-300 group relative fade-up"
      style={{ animationDelay: `${index * 80}ms` }}>
      <div className="relative aspect-square w-full overflow-hidden border-b border-white/10 bg-black">
        {current ? (
          <button type="button" onClick={() => setLightbox(true)} data-testid={`open-lightbox-${card.id}`}
            className="w-full h-full block cursor-zoom-in group/media">
            {current.type === "video" ? (
              <video src={imgUrl(current.url)} muted playsInline
                data-testid={`card-video-${card.id}`} className="w-full h-full object-contain" />
            ) : (
              <img src={imgUrl(current.url)} alt={card.title}
                className="w-full h-full object-contain transition-transform duration-500 group-hover/media:scale-[1.03]" />
            )}
            <span className="absolute bottom-2 right-2 z-10 bg-black/70 border border-white/20 text-white p-1.5 opacity-0 group-hover/media:opacity-100 transition-opacity">
              <Maximize2 size={14} />
            </span>
          </button>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700 font-mono text-xs">NO MEDIA</div>
        )}

        {media.length > 1 && (
          <>
            <button data-testid={`card-prev-${card.id}`} onClick={(e) => go(-1, e)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/70 border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] p-1.5 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <button data-testid={`card-next-${card.id}`} onClick={(e) => go(1, e)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/70 border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] p-1.5 transition-colors">
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
              {media.map((m, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setActive(i); }}
                  className={`w-2 h-2 border border-white/60 ${i === active ? "bg-[#FF3B30] border-[#FF3B30]" : "bg-transparent"}`}
                  aria-label={`media ${i + 1}`} />
              ))}
            </div>
          </>
        )}

        {isAdmin && (
          <div className="absolute top-3 right-3 z-20 flex gap-2">
            <span data-testid={`card-clicks-${card.id}`}
              className="flex items-center gap-1 bg-black/80 backdrop-blur border border-white/20 text-[#FF3B30] px-2 py-2 text-xs font-mono">
              <MousePointerClick size={13} /> {card.clicks ?? 0}
            </span>
            <button data-testid={`edit-card-${card.id}`} onClick={onEdit}
              className="bg-black/80 backdrop-blur border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] p-2 transition-colors">
              <Pencil size={14} />
            </button>
            <button data-testid={`delete-card-${card.id}`} onClick={onDelete}
              className="bg-black/80 backdrop-blur border border-white/20 hover:border-red-500 hover:text-red-500 p-2 transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="font-heading text-xl font-bold mb-2">{card.title}</h3>
        <p className="text-sm text-zinc-400 mb-6 flex-grow">{card.description}</p>
        {card.link_url ? (
          <a href={card.link_url} target="_blank" rel="noopener noreferrer" onClick={trackClick}
            data-testid={`affiliate-link-${card.id}`}
            className="flex items-center justify-center gap-2 w-full bg-white text-black font-bold py-3 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors">
            {card.button_label || "Go To"} <ExternalLink size={15} />
          </a>
        ) : (
          <span className="text-center w-full border border-white/10 text-zinc-600 py-3 uppercase tracking-wider text-sm font-mono">No link</span>
        )}
      </div>

      {/* Lightbox viewer */}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="bg-black/95 border border-white/15 rounded-none max-w-5xl w-[95vw] p-2 sm:p-4" data-testid={`lightbox-${card.id}`}>
          <DialogTitle className="sr-only">{card.title}</DialogTitle>
          <div className="relative flex items-center justify-center">
            {current && (current.type === "video" ? (
              <video src={imgUrl(current.url)} controls autoPlay playsInline className="max-h-[82vh] w-auto max-w-full" />
            ) : (
              <img src={imgUrl(current.url)} alt={card.title} className="max-h-[82vh] w-auto max-w-full object-contain" />
            ))}
            {media.length > 1 && (
              <>
                <button data-testid={`lightbox-prev-${card.id}`} onClick={(e) => go(-1, e)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/70 border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] p-2 transition-colors">
                  <ChevronLeft size={22} />
                </button>
                <button data-testid={`lightbox-next-${card.id}`} onClick={(e) => go(1, e)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/70 border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] p-2 transition-colors">
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </div>
          <p className="text-center font-mono text-xs uppercase tracking-widest text-zinc-400 pt-2">
            {card.title}{media.length > 1 ? ` — ${active + 1}/${media.length}` : ""}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
